/**
 * Minimal, dependency-free ZIP writer (STORE method — no compression).
 *
 * The app ships no zip library and the skills bundle is a handful of small text
 * files, so a stored archive is the right trade: a few dozen lines, no new
 * dependency, and every unzip tool reads it. Not a general archiver — UTF-8 text
 * files only, forward-slashed paths.
 */

const CRC32_TABLE: Uint32Array = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC32_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

// Fixed DOS timestamp (1980-01-01 00:00). The archive is content, not a moment
// in time, so a constant keeps the bytes deterministic.
const DOS_TIME = 0;
const DOS_DATE = 0x21;

interface Entry {
  nameBytes: Uint8Array;
  size: number;
  crc: number;
  offset: number;
}

export interface ZipFile {
  path: string;
  content: string;
}

export function createZip(files: ZipFile[]): Blob {
  const encoder = new TextEncoder();
  const parts: Uint8Array[] = [];
  const entries: Entry[] = [];
  let offset = 0;

  // Local file headers + file data.
  for (const file of files) {
    const nameBytes = encoder.encode(file.path);
    const data = encoder.encode(file.content);
    const crc = crc32(data);

    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x04034b50, true); // local file header signature
    view.setUint16(4, 20, true); // version needed to extract
    view.setUint16(6, 0, true); // general purpose flags
    view.setUint16(8, 0, true); // compression method: 0 = store
    view.setUint16(10, DOS_TIME, true);
    view.setUint16(12, DOS_DATE, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, data.length, true); // compressed size
    view.setUint32(22, data.length, true); // uncompressed size
    view.setUint16(26, nameBytes.length, true);
    view.setUint16(28, 0, true); // extra field length
    header.set(nameBytes, 30);

    entries.push({ nameBytes, size: data.length, crc, offset });
    parts.push(header, data);
    offset += header.length + data.length;
  }

  // Central directory.
  const centralStart = offset;
  for (const e of entries) {
    const rec = new Uint8Array(46 + e.nameBytes.length);
    const view = new DataView(rec.buffer);
    view.setUint32(0, 0x02014b50, true); // central directory header signature
    view.setUint16(4, 20, true); // version made by
    view.setUint16(6, 20, true); // version needed
    view.setUint16(8, 0, true); // flags
    view.setUint16(10, 0, true); // method
    view.setUint16(12, DOS_TIME, true);
    view.setUint16(14, DOS_DATE, true);
    view.setUint32(16, e.crc, true);
    view.setUint32(20, e.size, true); // compressed size
    view.setUint32(24, e.size, true); // uncompressed size
    view.setUint16(28, e.nameBytes.length, true);
    view.setUint16(30, 0, true); // extra field length
    view.setUint16(32, 0, true); // file comment length
    view.setUint16(34, 0, true); // disk number start
    view.setUint16(36, 0, true); // internal attributes
    view.setUint32(38, 0, true); // external attributes
    view.setUint32(42, e.offset, true); // relative offset of local header
    rec.set(e.nameBytes, 46);
    parts.push(rec);
    offset += rec.length;
  }
  const centralSize = offset - centralStart;

  // End of central directory record.
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, 0x06054b50, true); // EOCD signature
  ev.setUint16(4, 0, true); // number of this disk
  ev.setUint16(6, 0, true); // disk with central directory
  ev.setUint16(8, entries.length, true); // entries on this disk
  ev.setUint16(10, entries.length, true); // total entries
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, centralStart, true);
  ev.setUint16(20, 0, true); // comment length
  parts.push(eocd);

  return new Blob(parts as BlobPart[], { type: 'application/zip' });
}
